import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable, Subject, timer} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {
  StatusEventPageEventDisplayDetails
} from '@models/pages/status-event-page/status-event-page-event-display-details';
import {
  StatusEventPageSignalDataResponse
} from '@models/pages/status-event-page/status-event-page-signal-data-response';
import {retry, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {StatusEventType} from '@constants/machine/status-event-signal/status-event-type.enum';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TranslateService} from '@ngx-translate/core';
import {TimeInterval} from '@constants/pages/status-event-page/time-interval';
import {TranslationService} from '@services/translation/translation.service';
import {DatePipe} from '@angular/common';
import {
  StatusEventPagePastDataRequestParameters
} from '@models/pages/status-event-page/status-event-page-past-data-request-parameters';
import {RealtimePollingRequestParameters} from '@services/realtime-polling/realtime-polling-request-parameters';

@Injectable({
  providedIn: 'root'
})
export class StatusEventPageDataService {

  public static readonly POLLING_INTERVAL_SECONDS: number = 30;
  public stopPolling = new Subject();
  public GET_STATUS_EVENT_WIDGET_DATA_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId/widgetData/:timestamp/:sampling/:templateWidget';
  public GET_STATUS_EVENT_PAGE_DATA_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId/pageData/:timestamp/:sampling/:templatePage';

  private GET_PAST_REPORTS_PAGE_DATA_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId/pastData/:templatePage';
  private pollingForData$: Observable<any> = new Observable<any>();

  private readonly MILLISECONDS_MULTIPLIER = 1000;
  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private translateService: TranslateService,
              private translationService: TranslationService,
              private datePipe: DatePipe) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public formatDateTime(timestamp: number, format: string): string {
    const localeString = this.translationService.getActiveLanguage();
    const formattedDate = this.datePipe.transform(timestamp, format, undefined, localeString);
    if (!formattedDate) {
      return '';
    }
    return formattedDate;
  }

  public formatDate(timestamp: number): string {
    const localeString = this.translationService.getActiveLanguage();
    const formattedDate = this.datePipe.transform(timestamp, 'EEE d MMM y HH:mm:ss (ZZZZ)', undefined, localeString);
    if (!formattedDate) {
      return '';
    }
    return formattedDate;
  }

  public stopDataPolling() {
    this.stopPolling.next('');
  }

  public startDataPolling(machineId: string, pageId: string, isTemplatePage: boolean): Observable<any> {

    this.pollingForData$ = timer(1, StatusEventPageDataService.POLLING_INTERVAL_SECONDS * this.MILLISECONDS_MULTIPLIER).pipe(
        startWith(0),
        switchMap(() => this.getStatusEventPageData(machineId, pageId, 1, isTemplatePage)),
        retry(3),
        takeUntil(this.stopPolling)
    );
    return this.pollingForData$;

  }

  public getPastStatusEventPageData(machineId: string, pageId: string, parameters: StatusEventPagePastDataRequestParameters, isTemplatePage: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.GET_PAST_REPORTS_PAGE_DATA_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':pageId', pageId)

    .replace(':templatePage', String(isTemplatePage));

    return this.http.post(url, JSON.stringify(parameters), HTTPS_REQUEST_OPTIONS);
  }

  public getIntervalTypes() {
    return [
      {id: TimeInterval.LAST_HOUR, name: this.translateService.instant('Last hour')},
      {id: TimeInterval.CURRENT_DAY, name: this.translateService.instant('Current day')},
      {id: TimeInterval.YESTERDAY, name: this.translateService.instant('Yesterday')},
      {id: TimeInterval.LAST_7_DAYS, name: this.translateService.instant('Last 7 days')},
      {id: TimeInterval.CUSTOM, name: this.translateService.instant('Custom')}
    ];
  }

  public getIntervals(timeInterval: TimeInterval) {
    let startInterval: Date = new Date();
    let endInterval: Date = new Date();
    const now = new Date();
    switch (timeInterval) {
      case TimeInterval.LAST_HOUR:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() - 1, now.getMinutes(), 0, 0));
        break;
      case TimeInterval.CURRENT_DAY:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
        break;
      case TimeInterval.YESTERDAY:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, -1));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0));
        break;
      case TimeInterval.LAST_7_DAYS:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0));
        break;

    }

    startInterval.setMinutes(startInterval.getMinutes() + now.getTimezoneOffset());
    endInterval.setMinutes(endInterval.getMinutes() + now.getTimezoneOffset());
    return {
      endInterval, startInterval
    };
  }

  public getDate(event): number {
    const date = new Date(Number(event.date));

    date.setHours(Number(event.time.hour));
    date.setMinutes(Number(event.time.minute));
    date.setSeconds(0);
    return date.getTime();
  }

  public parseStatusEventResponseToDisplayDetails(statusEventPageConfiguration: StatusEventPage, response: Map<string, StatusEventPageSignalDataResponse[]>, visibleStatusEventTypes: StatusEventType[], visibleSignals: string[]): StatusEventPageEventDisplayDetails[] {
    const result: StatusEventPageEventDisplayDetails[] = [];
    if (!response || response.size === 0) {
      return result;
    }

    for (const signalName of Object.keys(response)) {
      if (!this.shouldShowSignal(signalName, visibleSignals)) {
        continue;
      }

      const signalDetails = response[signalName];
      const signalLabel = this.findConfigurationSignalLabelByName(signalName, statusEventPageConfiguration);

      for (const detail of signalDetails) {
        if (detail) {
          for (const status of detail.values) {
            const item: StatusEventPageEventDisplayDetails = new StatusEventPageEventDisplayDetails(signalName, signalLabel, detail.ts, status.code, status.type, status.description, status.metadata, status.localizations);
            if (this.shouldShowItemByEventTypes(item, visibleStatusEventTypes)) {
              result.push(item);
            }
          }
        }

      }
    }

    return result;
  }

  public parseStatusEventResponseToDisplayWidgetDetails(statusEventPageConfiguration: StatusEventPage, response: Map<string, StatusEventPageSignalDataResponse[]>): StatusEventPageEventDisplayDetails[] {
    const result: StatusEventPageEventDisplayDetails[] = [];
    if (!response || response.size === 0) {
      return result;
    }

    for (const signalName of Object.keys(response)) {
      const signalDetails = response[signalName];
      const signalLabel = this.findConfigurationSignalLabelByName(signalName, statusEventPageConfiguration);
      for (const detail of signalDetails) {
        let index = 0;
        if (detail.values.length - 1 > 0) {
          index = detail.values.length - 1;
        }
        const latestValue = detail.values[index];
        if (latestValue) {
          const item: StatusEventPageEventDisplayDetails = new StatusEventPageEventDisplayDetails(signalName, signalLabel, detail.ts, latestValue.code, latestValue.type, latestValue.description, latestValue.metadata, latestValue.localizations);
          result.push(item);
        }
      }
    }

    return result;
  }

  public splitMetaDataInColumns(columns: number, details: StatusEventPageEventDisplayDetails): Map<number, string[]> {
    const columnsMap: Map<number, string[]> = new Map<number, string[]>();
    if (!details) {
      return columnsMap;
    }
    const metadata = details.metadata;
    if (!metadata) {
      return columnsMap;
    }
    const array = Object.keys(metadata);

    for (let i = 0; i < array.length; i++) {
      const columnKey: number = i % columns;

      let strings = columnsMap.get(columnKey);
      if (!strings) {
        strings = [];
      }

      columnsMap.set(columnKey, [...strings, array[i]]);
    }
    return columnsMap;
  }

  public getLocalizedDescriptionOrDefault(language: string, details: StatusEventPageEventDisplayDetails): string {
    let defaultDescription = 'null';
    if (!details) {
      return defaultDescription;
    }
    if (details.description && !details.description?.isNullOrWhitespace()) {
      defaultDescription = details.description;
    }


    if (language.isNullOrWhitespace()) {
      return defaultDescription;
    }

    const localizations = details.localizations;
    if (!localizations) {
      return defaultDescription;
    }

    const description = localizations[language];
    if (!description || description.isNullOrWhitespace()) {
      return defaultDescription;
    }
    return description;
  }

  private findConfigurationSignalLabelByName(name: string, statusEventPageConfiguration: StatusEventPage) {
    if (!statusEventPageConfiguration?.statusEventsDetails) {
      return name;
    }
    const statusEventsDetails = statusEventPageConfiguration.statusEventsDetails;
    const signalIds: string[] = Object.keys(statusEventsDetails);
    for (const element of signalIds) {
      const details = statusEventsDetails[Number(element)];
      if (details && details.name === name) {
        return details.label;
      }
    }
    return name;
  }

  public getStatusWidgetInitialData(parameters: RealtimePollingRequestParameters): Observable<any> {

    const machineId = String(parameters.machineId);
    const pageId = String(parameters.pageId);
    let timestamp = '0';
    if (parameters.timestamp && parameters.timestamp > 0) {
      timestamp = String(parameters.timestamp);
    }
    const sampling = String(parameters.sampling);
    const isTemplateWidget = String(parameters.templateWidget);

    let url: string = this.BASE_URL + this.GET_STATUS_EVENT_WIDGET_DATA_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    url = url.replace(':pageId', pageId);
    url = url.replace(':timestamp', timestamp);
    url = url.replace(':sampling', sampling);
    url = url.replace(':templateWidget', isTemplateWidget);

    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  private getStatusEventPageData(machineId: string, pageId: string, sampling: number, isTemplatePage: boolean, timestamp?: number): Observable<any> {
    this.loadingIndicator.show();
    if (!timestamp || timestamp <= 0) {
      timestamp = 0;
    }

    let url: string = this.BASE_URL + this.GET_STATUS_EVENT_PAGE_DATA_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    url = url.replace(':pageId', pageId);
    url = url.replace(':timestamp', String(timestamp));
    url = url.replace(':sampling', String(sampling));
    url = url.replace(':templatePage', String(isTemplatePage));

    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  private shouldShowItemByEventTypes(item: StatusEventPageEventDisplayDetails, visibleStatusEventTypes: StatusEventType[]): boolean {
    if (!visibleStatusEventTypes || visibleStatusEventTypes.length === 0) {
      return true;
    }

    return visibleStatusEventTypes.includes(item.type);
  }

  private shouldShowSignal(signalName: string, visibleSignals: string[]): boolean {
    if (!visibleSignals || visibleSignals.length === 0) {
      return true;
    }
    return visibleSignals.includes(signalName);

  }

}
